import _ from "lodash";
import { action, computed, decorate, observable, when } from "mobx";
import {
  FREQUENCY_DISPLAY_NAMES,
  PLAY_ORDERING_TYPE_DISPLAY_NAMES,
  RULE_FORM_FIELD_LABELS,
  VIDEO_FORMAT_OPTIONS,
  FEED_EXCLUSION_OPTIONS,
  DEFAULT_UNSET_WATERMARK,
  DEFAULT_UNSET_CAPTIONS,
  DEFAULT_UNSET_CROP_RATIO,
  CROP_RATIO_OPTIONS
} from "utils/constants";

const FEED_EXCLUSIONS_LABEL_MAP = _.reduce(
  FEED_EXCLUSION_OPTIONS,
  (acc, o) => ({ ...acc, [o.value]: o.label }),
  {}
);
const VIDEO_FORMAT_LABEL_MAP = _.reduce(
  VIDEO_FORMAT_OPTIONS,
  (acc, o) => ({ ...acc, [o.value]: o.label }),
  {}
);

// Array of notifications types that do not require a destination value
// when creating a rule.
const NOTIFICATION_DESTINATION_EXCLUSIONS = ["deltatre", "deltatre_milb"];

export const DEFAULT_NOTIFICATIONS = {
  slack: {
    enabled: false,
    destination: "",
    message: ""
  },
  email: {
    enabled: false,
    destination: "",
    message: ""
  },
  twitter: {
    enabled: false,
    destination: ""
  },
  youtube: {
    enabled: false,
    destination: ""
  },
  deltatre: {
    enabled: false,
    destination: "",
    destinationOptions: {
      ENABLE_PUSH_NOTIFICATIONS: "false",
      AUTO_PUBLISH_EXCLUSIONS: "",
      OMIT_DESCRIPTION: "false"
    }
  },
  deltatre_milb: {
    enabled: false,
    destination: "",
    destinationOptions: {
      ENABLE_PUSH_NOTIFICATIONS: "false",
      AUTO_PUBLISH_EXCLUSIONS: "",
      OMIT_DESCRIPTION: "false"
    }
  }
};

const DEFAULT_WORKFLOW = "STANDARD";
export const DEFAULT_VIDEO_OPTIONS = {
  types: ["VIDEO"],
  feedPreference: "",
  feedExclusions: [],
  orderingType: "NONE",
  limitCount: null,
  limitOption: "NONE",
  creationOptions: [],
  workflow: DEFAULT_WORKFLOW,
  watermark: null,
  captions: null,
  cropRatio: null
};

const DEFAULT_ERRORS = {
  name: false,
  query: false,
  limitCount: false,
  videoCreationOptions: false,
  autoPublish: false
};

class RuleStore {
  ruleId = "";
  name = "";
  owner = "";
  frequency = "";
  catchupStyle = "";
  query = "";
  enabled = true;
  videoOptions = DEFAULT_VIDEO_OPTIONS;
  notifications = DEFAULT_NOTIFICATIONS;
  errors = DEFAULT_ERRORS;
  videoCreationOptionsErrorMsg = [];
  saveButtonDisabled = false;
  cropRatios = Object.entries(CROP_RATIO_OPTIONS).map(([label, value]) => ({
    value,
    label
  }));

  constructor(sportyApi, pitsApi, sportyMetadataStore, authStore) {
    this.sportyApi = sportyApi;
    this.pitsApi = pitsApi;
    this.sportyMetadataStore = sportyMetadataStore;
    this.authStore = authStore;
  }

  isTrue(inputValue) {
    return inputValue === "true";
  }

  setName(name) {
    this.name = name;
  }

  setFrequency(frequency) {
    this.frequency = frequency;
    this._validateVideoCreationOptions();
  }

  setQuery(query) {
    this.query = query;
  }

  setNotificationEnabled(type, isEnabled) {
    if (this.notifications[type]) {
      this.notifications[type].enabled = isEnabled;
    }
  }

  setNotificationDestination(type, destination) {
    if (this.notifications[type]) {
      this.notifications[type].destination = destination;
    }
  }

  setNotificationMessage(type, message) {
    if (this.notifications[type]) {
      this.notifications[type].message = message;
    }
  }

  setPushNotifications(type, checked) {
    this.setBooleanDestinationOption(
      type,
      "ENABLE_PUSH_NOTIFICATIONS",
      checked
    );
  }

  setOmitDescription(type, checked) {
    this.setBooleanDestinationOption(type, "OMIT_DESCRIPTION", checked);
  }

  setBooleanDestinationOption(type, option, checked) {
    this.notifications[type].destinationOptions[option] = checked
      ? "true"
      : "false";
  }

  setWorkflow(workflow) {
    this.videoOptions.workflow = workflow;
  }

  setVideoTypes(types) {
    const values = types.map(o => o.value);
    if (!values.includes("VIDEO")) {
      this._clearVideoCreationOptions();
    }

    this.videoOptions.types = values;
  }

  setVideoFeedPreference(feed) {
    this.videoOptions.feedPreference = feed;
  }

  setPlayOrderingType(orderingType) {
    this.videoOptions.orderingType = orderingType;
    this._validateVideoCreationOptions();
  }

  setVideoLimitOption(limitBy) {
    this.videoOptions.limitOption = limitBy;

    // Reset the limit count option when limit option is NONE
    if (this.isLimitCountDisabled) {
      this.errors.limitCount = false;
      this.videoOptions.limitCount = DEFAULT_VIDEO_OPTIONS.limitCount;
    }
  }

  setVideoLimitCount(limitCount) {
    this.videoOptions.limitCount = limitCount;
  }

  setVideoCreationOptions(selectedItems) {
    this.videoOptions.creationOptions = selectedItems;
    this._validateVideoCreationOptions();
  }

  setVideoWatermark(watermark) {
    if (watermark !== DEFAULT_UNSET_WATERMARK) {
      this.videoOptions.watermark = { filename: watermark };
    } else {
      this.videoOptions.watermark = null;
    }
  }

  setVideoCaptions(language) {
    if (language !== DEFAULT_UNSET_CAPTIONS) {
      this.videoOptions.captions = { language: language };
    } else {
      this.videoOptions.captions = null;
    }
  }

  setVideoCropRatio(cropRatio) {
    if (cropRatio !== DEFAULT_UNSET_CROP_RATIO) {
      this.videoOptions.cropRatio = { value: cropRatio };
    } else {
      this.videoOptions.cropRatio = null;
    }
  }

  setEnabled(enabled) {
    this.enabled = enabled;
  }

  setNetworkFeedExclusion(checked) {
    if (checked) {
      this.videoOptions.feedExclusions = ["NETWORK"];
    } else {
      this.videoOptions.feedExclusions = [];
    }
  }

  setAutoPublishExclusions(type, selected) {
    const values = selected.map(o => o.value);
    const currentPushNotifications = this.notifications[type].destinationOptions
      .ENABLE_PUSH_NOTIFICATIONS;
    this.notifications[type].destinationOptions = {
      AUTO_PUBLISH_EXCLUSIONS: values.join(","),
      ENABLE_PUSH_NOTIFICATIONS: currentPushNotifications
    };
  }

  enableSaveButton() {
    this.saveButtonDisabled = false;
  }

  disableSaveButton() {
    this.saveButtonDisabled = true;
  }

  clear() {
    this.ruleId = "";
    this.name = "";
    this.frequency = "";
    this.catchupStyle = "";
    this.query = "";
    this.notifications = DEFAULT_NOTIFICATIONS;
    this.videoOptions = DEFAULT_VIDEO_OPTIONS;
    this.enabled = true;
    this.saveButtonDisabled = false;
  }

  resetRuleAs(id) {
    this.sportyApi.getRule(id).then(rule => {
      this.ruleId = rule.ruleId;
      this.name = rule.name;
      this.owner = rule.username;
      this.frequency = rule.frequency;
      if (rule.injectInGameFields) {
        this.catchupStyle = "INNING_ONLY";
      } else {
        this.catchupStyle = "FULL_GAME";
      }
      this.query = rule.baseQuery;
      this.enabled = rule.enabled;

      if (!_.isEmpty(rule.videoOptions)) {
        let videoOptions = { ...rule.videoOptions, creationOptions: [] };
        if (!videoOptions.orderingType) videoOptions.orderingType = "NONE";
        if (!videoOptions.limitCount) videoOptions.limitCount = null;
        if (!videoOptions.watermark || !videoOptions.watermark.filename)
          videoOptions.watermark = null;
        if (!videoOptions.captions || !videoOptions.captions.language)
          videoOptions.captions = null;
        if (!videoOptions.cropRatio || !videoOptions.cropRatio.value)
          videoOptions.cropRatio = null;
        this.videoOptions = videoOptions;

        if (this.videoOptions.options.length) {
          when(
            () => _.size(this.sportyMetadataStore.videoCreationOptions),
            () => {
              let videoCreationOptions = [];
              this.videoOptions.options.forEach(x => {
                if (this.sportyMetadataStore.videoCreationOptions[x.name]) {
                  videoCreationOptions.push({
                    value: x.name,
                    label: this.sportyMetadataStore.videoCreationOptions[x.name]
                  });
                }
              });
              this.videoOptions.creationOptions = videoCreationOptions;
              delete this.videoOptions.options;
            }
          );
        }
      } else {
        this.videoOptions = DEFAULT_VIDEO_OPTIONS;
      }

      this.notifications = DEFAULT_NOTIFICATIONS;
      rule.notifications.forEach(notification => {
        const key = _.toLower(notification.type);
        this.notifications[key].enabled = true;
        this.notifications[key].destination = notification.destination;
        this.notifications[key].message = notification.message;

        const mappedDestinationOptions = {};
        (notification.destinationOptions || []).forEach(obj => {
          mappedDestinationOptions[obj.type] = obj.value;
        });
        this.notifications[key].destinationOptions = {
          ...this.notifications[key].destinationOptions,
          ...mappedDestinationOptions
        };
      });
    });
  }

  async putRule() {
    this.owner = this.authStore.user.preferred_username;
    return this.validateForm().then(() => {
      if (this.isFormValid) {
        return this.sportyApi.putRule(this.sportyRequestBody);
      } else {
        return Promise.reject(new Error("Form is invalid"));
      }
    });
  }

  validateName() {
    this.errors.name = this.name.length === 0;
  }

  validateQuery() {
    return this.pitsApi.getQueryInfo([this.query]).then(response => {
      const queryResult = _.first(response);
      if (queryResult) {
        this.errors.query = !queryResult.valid;
      }
    });
  }

  validateLimitCount() {
    if (this.isLimitCountDisabled) {
      this.errors.limitCount = false;
    } else {
      this.errors.limitCount = Number(this.videoOptions.limitCount) <= 0;
    }
  }

  validateAutoPublishExclusionTags() {
    const deltatre = this.notifications["deltatre"];
    const milb_deltatre = this.notifications["deltatre_milb"];
    if (
      deltatre.destinationOptions.AUTO_PUBLISH_EXCLUSIONS.length > 0 &&
      deltatre.destination === "" &&
      deltatre.enabled
    ) {
      this.errors.deltatreAutoPublish = true;
    } else {
      this.errors.deltatreAutoPublish = false;
    }

    if (
      milb_deltatre.destinationOptions.AUTO_PUBLISH_EXCLUSIONS.length > 0 &&
      milb_deltatre.destination === "" &&
      milb_deltatre.enabled
    ) {
      this.errors.milbDeltatreAutoPublish = true;
    } else {
      this.errors.milbDeltatreAutoPublish = false;
    }
  }

  _validateVideoCreationOptions() {
    return new Promise(
      function(resolve, reject) {
        // clear error when no items selected
        if (this.videoOptions.creationOptions.length === 0) {
          this.videoCreationOptionsErrorMsg = [];
          this.errors.videoCreationOptions = false;
        }

        if (this.frequency && this.videoOptions.creationOptions.length) {
          this.sportyApi
            .validateVideoCreationOptions(
              this.frequency,
              this.videoOptions.creationOptions.map(x => x.value),
              this.videoOptions.orderingType
            )
            .then(errors => {
              let selectedErrors = [];
              errors.forEach((item, i, self) => {
                if (
                  item.hasOwnProperty("incompatibleValues") &&
                  item.incompatibleValues != null
                ) {
                  // filter out mutually exclusive items which precede current item
                  const exclusivePrecedingItems = self
                    .slice(0, i)
                    .filter(x => _.includes(x.incompatibleValues, item.value));
                  item.incompatibleValues = _.difference(
                    item.incompatibleValues,
                    exclusivePrecedingItems.map(x => x.value)
                  );
                  if (item.incompatibleValues.length) {
                    selectedErrors.push(item);
                  }
                } else {
                  selectedErrors.push(item);
                }
              });
              this.videoCreationOptionsErrorMsg = [];
              selectedErrors.forEach(item => {
                if (_.has(item, "incompatibleValues")) {
                  this.videoCreationOptionsErrorMsg.push(
                    [
                      this.sportyMetadataStore.videoCreationOptions[item.value],
                      ": incompatible with [",
                      item.incompatibleValues.map(
                        x =>
                          `"${this.sportyMetadataStore.videoCreationOptions[
                            x
                          ].toString()}"`
                      ),
                      "]"
                    ].join("")
                  );
                } else if (_.has(item, "incompatibleFields")) {
                  let errorMsg = "";
                  if (item.incompatibleFields.indexOf("ruleFrequency") > -1) {
                    errorMsg += `${RULE_FORM_FIELD_LABELS.FREQUENCY}="${
                      FREQUENCY_DISPLAY_NAMES[this.frequency]
                    }"`;
                  }

                  if (item.incompatibleFields.indexOf("orderingType") > -1) {
                    if (errorMsg) {
                      errorMsg += " and ";
                    }
                    errorMsg += `${RULE_FORM_FIELD_LABELS.ORDERING_TYPE}="${
                      PLAY_ORDERING_TYPE_DISPLAY_NAMES[
                        this.videoOptions.orderingType
                      ]
                    }"`;
                  }

                  if (errorMsg) {
                    this.videoCreationOptionsErrorMsg.push(
                      [
                        this.sportyMetadataStore.videoCreationOptions[
                          item.value
                        ],
                        ": incompatible with ",
                        errorMsg
                      ].join("")
                    );
                  }
                }
              });
              this.errors.videoCreationOptions =
                this.videoCreationOptionsErrorMsg.length > 0;

              resolve();
            })
            .catch(err => {
              reject(err);
            });
        } else {
          // If this condition is met we need to resolve the function or the execution will
          // hang.
          resolve();
        }
      }.bind(this)
    );
  }

  validateForm() {
    this.validateName();
    this.validateLimitCount();
    this.validateAutoPublishExclusionTags();
    return Promise.all([
      this.validateQuery(),
      this._validateVideoCreationOptions()
    ]);
  }

  get isLimitCountDisabled() {
    return this.videoOptions.limitOption === "NONE";
  }

  get injectInGameFields() {
    return this.catchupStyle === "INNING_ONLY";
  }

  get sportyRequestBody() {
    const notifications = _.chain(this.notifications)
      .map((notification, type) => {
        if (
          !notification.enabled ||
          (!notification.destination &&
            !NOTIFICATION_DESTINATION_EXCLUSIONS.includes(type))
        ) {
          return undefined;
        }

        const destinationOptions = [];
        Object.keys(notification.destinationOptions || {}).forEach(key => {
          destinationOptions.push({
            type: key,
            value: notification.destinationOptions[key]
          });
        });

        return {
          type: _.toUpper(type),
          destination: notification.destination,
          message: notification.message || "",
          destinationOptions
        };
      })
      .compact()
      .value();

    let sportyVideoOptions = { ...this.videoOptions };
    sportyVideoOptions.options = sportyVideoOptions.creationOptions.map(x => {
      return { name: x.value, value: true };
    });
    delete sportyVideoOptions.creationOptions;
    if (sportyVideoOptions.orderingType === "NONE") {
      delete sportyVideoOptions.orderingType;
    }

    const ruleBody = {
      ruleId: this.ruleId,
      name: this.name,
      username: this.owner,
      frequency: this.frequency,
      baseQuery: this.query,
      notifications,
      videoOptions: sportyVideoOptions,
      enabled: this.enabled
    };
    if (_.includes(["INNING_END", "HALF_INNING_END"], this.frequency)) {
      ruleBody.injectInGameFields = this.injectInGameFields;
    }
    return ruleBody;
  }

  get isFormValid() {
    return !_.some(this.errors);
  }

  get showVideoCreationOptions() {
    return this.videoOptions.types.includes("VIDEO");
  }

  get videoTypesWithLabels() {
    return this.videoOptions.types.map(value => ({
      label: VIDEO_FORMAT_LABEL_MAP[value],
      value: value
    }));
  }

  get workflowHelperText() {
    return this.sportyMetadataStore.workflowDetails[this.videoOptions.workflow];
  }

  autoPublishExclusionsWithLabels(type) {
    const exclusions = this.notifications[type].destinationOptions
      .AUTO_PUBLISH_EXCLUSIONS;
    if (exclusions) {
      return exclusions.split(",").map(value => ({
        label: FEED_EXCLUSIONS_LABEL_MAP[value],
        value: value
      }));
    } else {
      return [];
    }
  }

  _clearVideoCreationOptions() {
    this.errors.videoCreationOptions = false;
    this.videoCreationOptionsErrorMsg = [];
    this.videoOptions.creationOptions = [];
  }

  get excludeNetworkFeed() {
    return this.videoOptions.feedExclusions.includes("NETWORK");
  }
}

decorate(RuleStore, {
  name: observable,
  owner: observable,
  frequency: observable,
  catchupStyle: observable,
  query: observable,
  notifications: observable,
  videoOptions: observable,
  errors: observable,
  videoCreationOptionsErrorMsg: observable,
  enabled: observable,
  saveButtonDisabled: observable,
  setName: action,
  setOwner: action,
  setFrequency: action,
  setQuery: action,
  setNotificationEnabled: action,
  setNotificationDestination: action,
  setNotificationMessage: action,
  setVideoFeedPreference: action,
  setPlayOrderingType: action,
  setWorkflow: action,
  setVideoLimitOption: action,
  setVideoLimitCount: action,
  setVideoWatermark: action,
  setVideoCaptions: action,
  setVideoCropRatio: action,
  setEnabled: action,
  validateForm: action,
  validateQuery: action,
  clear: action,
  showVideoCreationOptions: computed,
  isFormValid: computed,
  isLimitCountDisabled: computed,
  sportyRequestBody: computed,
  injectInGameFields: computed,
  excludeNetworkFeed: computed,
  workflowHelperText: computed,
  videoTypesWithLabels: computed
});

export default RuleStore;



// WEBPACK FOOTER //
// ./src/stores/RuleStore.js